import {useAppSelector} from '../../../store/store';
import buttonStyles from '../../../components/button/button.module.scss';
import formStyles from '../../../components/form/form.module.scss';
import {Button} from '../../../components/button/button';
import {ButtonColorType} from '../../../enums/button_color_type.ts';
import {useState} from 'react';
import {Status} from '../../../enums/status.ts';
import {CenteredSpinner} from '../../../components/spinner/centered_spinner.tsx';
import {AvailabilityEnum, AvailabilityType} from '../../../gql/pwa/graphql.ts';
import styles from './date_picker.module.scss';
import {addMonths, format, getDaysInMonth, isSameDay, parse, setDate, startOfMonth, subMonths} from 'date-fns';
import {classNames} from '../../../support/classnames.ts';
import {__} from '../../../support/lang/translations.ts';
import {AltButton} from '../../../components/button/alt_button.tsx';
import altButtonStyles from '../../../components/button/alt_button.module.scss';
import {HideElement} from '../../../components/utility/hide_element_wrapper.tsx';
import {ShowElement} from '../../../components/utility/show_element_wrapper.tsx';

export function DatePickerTitle() {
    return (
        <>
            <h1>{__('appointment.date_picker.title')}</h1>
            <h2>{__('appointment.date_picker.subtitle')}</h2>
        </>
    );
}

interface ContentProps {
    availabilities: AvailabilityType[];
    date: Date | null;
    onDateChange: (date: Date) => void;
}

function emptyStartCount(date: Date): number {
    const firstDayInMonth = startOfMonth(date);
    const firstDayNumber = firstDayInMonth.getDay() === 0 ? 7 : firstDayInMonth.getDay();

    return firstDayNumber - 1;
}

function emptyEndCount(date: Date): number {
    const firstDayInMonth = startOfMonth(date);
    const firstDayNumber = firstDayInMonth.getDay() === 0 ? 7 : firstDayInMonth.getDay();
    const daysInMonth = getDaysInMonth(date);

    return 7 - ((firstDayNumber - 1 + daysInMonth) % 7);
}

function availabilitiesForMonth(availabilities: AvailabilityType[], month: Date): AvailabilityType[] {
    if (availabilities.length === 0) {
        return [];
    }
    const daysInMonth = getDaysInMonth(month);

    return Array.from({length: daysInMonth}).map((_, i) => {
        const date = setDate(month, i + 1);
        const availability = availabilities.find((a) => {
            return a.date && isSameDay(date, parse(a.date, 'yyyy-MM-dd', month));
        });
        if (availability) {
            return availability;
        }
        return {
            date: format(date, 'yyyy-MM-dd'),
            availability: AvailabilityEnum.Closed,
        };
    });
}

export function DatePickerContent(props: ContentProps) {
    const customerInfo = useAppSelector((state) => state.customerInfo);
    const now = new Date();
    const [date, setDate] = useState(now);
    const isPast = subMonths(date, 1) < startOfMonth(now);
    const isTooMuchFuture = addMonths(date, 1) > startOfMonth(addMonths(now, 6));

    if (customerInfo.status !== Status.FULFILLED) {
        return <CenteredSpinner />;
    }

    return (
        <>
            <h3>{__('appointment.date_picker.title_appointment')}</h3>
            <div className={styles.date_picker}>
                <div className={styles.date_picker__header}>
                    <AltButton
                        className={classNames(altButtonStyles.icon, altButtonStyles.no_border)}
                        type="button"
                        onClick={() => {
                            setDate(subMonths(date, 1));
                        }}
                        disabled={isPast}
                    >
                        <i className="fa-light fa-chevron-left" />
                    </AltButton>

                    {/*TODO: Locale*/}
                    <div className={styles.header_month}>{format(date, 'MMMM')}</div>

                    <AltButton
                        className={classNames(altButtonStyles.icon, altButtonStyles.no_border)}
                        type="button"
                        onClick={() => {
                            setDate(addMonths(date, 1));
                        }}
                        disabled={isTooMuchFuture}
                    >
                        <i className="fa-light fa-chevron-right" />
                    </AltButton>
                </div>
                <div className={classNames(formStyles.form, formStyles.form_full)}>
                    <div className={styles.date_picker__grid}>
                        <>
                            {['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'].map((day) => {
                                return (
                                    <div className={styles.date_picker__weekday} key={'day-' + day}>
                                        {day}
                                    </div>
                                );
                            })}
                        </>
                    </div>
                    <div className={styles.date_picker__grid}>
                        {Array.from({length: emptyStartCount(date)}).map((_, index) => {
                            return <div key={'empty-' + index} className={classNames(styles.date_picker_date)}></div>;
                        })}
                        {availabilitiesForMonth(props.availabilities, date).map((availability) => {
                            const availabilityDate =
                                (availability.date !== undefined &&
                                    availability.date !== null &&
                                    parse(availability.date, 'yyyy-MM-dd', date)) ||
                                null;
                            return (
                                <div
                                    className={classNames(styles.date_picker_date, {
                                        [styles.date_picker_date_available]:
                                            availability.availability === AvailabilityEnum.Available,
                                        [styles.date_picker_date_partially_available]:
                                            availability.availability === AvailabilityEnum.Partially,
                                        [styles.date_picker_date_unavailable]:
                                            availability.availability === AvailabilityEnum.Unavailable,
                                        [styles.date_picker_date_closed]:
                                            availability.availability === AvailabilityEnum.Closed,
                                        [styles.date_picker_date_selected]:
                                            availabilityDate !== undefined &&
                                            availabilityDate !== null &&
                                            props.date !== null &&
                                            isSameDay(availabilityDate, props.date),
                                    })}
                                    key={'availability-' + availability.date}
                                    onClick={() => availabilityDate && props.onDateChange(availabilityDate || null)}
                                >
                                    {availabilityDate && format(availabilityDate, 'dd')}
                                </div>
                            );
                        })}
                        {Array.from({length: emptyEndCount(date)}).map((_, index) => {
                            return <div key={'empty-' + index} className={classNames(styles.date_picker_date)}></div>;
                        })}
                    </div>
                    <div className={styles.date_picker__legend}>
                        <label className={styles.legend_label}>
                            <span className={styles.available} />
                            {__('appointment.date_picker.legend.status.available')}
                        </label>
                        <label className={styles.legend_label}>
                            <span className={styles.partially_available} />
                            {__('appointment.date_picker.legend.status.partially')}
                        </label>
                        <label className={styles.legend_label}>
                            <span className={styles.unavailable} />
                            {__('appointment.date_picker.legend.status.unavailable')}
                        </label>
                    </div>
                </div>
            </div>
        </>
    );
}

interface FooterProps {
    onPreviousSlide: () => void;
    onNextSlide: () => void;
    nextIsDisabled: boolean;
}

export function DatePickerFooter(props: FooterProps) {
    return (
        <>
            <ShowElement size={'sm'}>
                <Button
                    className={buttonStyles.button_step}
                    type="button"
                    onClick={() => props.onPreviousSlide()}
                    colorType={ButtonColorType.Black}
                >
                    <i className="fa-light fa-chevron-left" />
                    <span>{__('appointment.date_picker.footer.buttons.previous')}</span>
                </Button>
            </ShowElement>

            <HideElement size={'sm'}>
                <span />
            </HideElement>

            <Button
                className={buttonStyles.button_step}
                disabled={props.nextIsDisabled}
                type="button"
                onClick={() => props.onNextSlide()}
                colorType={ButtonColorType.Green}
            >
                <i className="fa-light fa-check" />
                <span>{__('appointment.date_picker.footer.buttons.next')}</span>
            </Button>
        </>
    );
}
